import React, { Suspense, lazy, useEffect, useState } from "react";
import { BrowserRouter as Router, Navigate, NavLink, Route, Routes } from "react-router-dom";

// Eagerly loaded: the first screen every user sees (LoginPage) and the
// default landing page for most roles (ShipmentEntry, via HomeRoute) -
// lazy-loading these would put a loading flash right where it's most
// noticeable. Every other page is lazy so a visit to one page doesn't pay
// the download/parse cost of the other ~20 pages it doesn't need.
import ShipmentEntry from "./pages/ShipmentEntry";
import LoginPage from "./pages/LoginPage";
import CheckInReminderBanner from "./components/CheckInReminderBanner";
import API, { buildBackendPageUrl, setAuthToken } from "./services/api";

import "bootstrap/dist/css/bootstrap.min.css";

const SkartBookingPage = lazy(() => import("./pages/SkartBookingPage"));
const SkartActivityPage = lazy(() => import("./pages/SkartActivityPage"));
const SkartPayloadPage = lazy(() => import("./pages/SkartPayloadPage"));
const ShipmentDashboard = lazy(() => import("./pages/ShipmentDashboard"));
const Shipment360Page = lazy(() => import("./pages/Shipment360Page"));
const VendorRateUpload = lazy(() => import("./pages/VendorRateUpload"));
const MasterData = lazy(() => import("./pages/MasterData"));
const CostPriceEntry = lazy(() => import("./pages/CostPriceEntry"));
const SellingPriceEntry = lazy(() => import("./pages/SellingPriceEntry"));
const PricingConsolePage = lazy(() => import("./pages/PricingConsolePage"));
const PricingOperationsPage = lazy(() => import("./pages/PricingOperationsPage"));
const SalesTrackerPage = lazy(() => import("./pages/SalesTrackerPage"));
const VendorInvoiceConsolePage = lazy(() => import("./pages/VendorInvoiceConsolePage"));
const AccountsReceivablePage = lazy(() => import("./pages/AccountsReceivablePage"));
const InvoicingPage = lazy(() => import("./pages/InvoicingPage"));
const UserManagementPage = lazy(() => import("./pages/UserManagementPage"));
const ShipmentTrackingPage = lazy(() => import("./pages/ShipmentTrackingPage"));
const ShipmentTrackingInTransitPage = lazy(() => import("./pages/ShipmentTrackingInTransitPage"));
const ManualStatusBoardPage = lazy(() => import("./pages/ManualStatusBoardPage"));
const StatusEmailTemplatesPage = lazy(() => import("./pages/StatusEmailTemplatesPage"));
const WhatsappTemplatesPage = lazy(() => import("./pages/WhatsappTemplatesPage"));
const FedExTrackerPage = lazy(() => import("./pages/FedExTrackerPage"));
const TaskBoardPage = lazy(() => import("./pages/TaskBoardPage"));
const TicketingToolPage = lazy(() => import("./pages/TicketingToolPage"));
const AttendancePage = lazy(() => import("./pages/AttendancePage"));
const RateCardGeneratorPage = lazy(() => import("./pages/RateCardGeneratorPage"));
const InxpressBookingsPage = lazy(() => import("./pages/InxpressBookingsPage"));
const BookingQueuePage = lazy(() => import("./pages/BookingQueuePage"));
const OpsTvPage = lazy(() => import("./pages/OpsTvPage"));
const PendingPickupPage = lazy(() => import("./pages/PendingPickupPage"));
const ChecklistsPage = lazy(() => import("./pages/ChecklistsPage"));
const OpsSummaryPage = lazy(() => import("./pages/OpsSummaryPage"));

const NAV_ITEMS = [
  { key: "express-rate-calculator", label: "Rate Calculator", external: true, href: "/express-rate-calculator" },
  { key: "shipment-entry", to: "/", label: "Shipment Entry", end: true },
  { key: "booking-queue", to: "/booking-queue", label: "Booking Queue" },
  { key: "pending-pickup", to: "/pending-pickup", label: "Pending Pickup" },
  { key: "checklists", to: "/checklists", label: "Checklists" },
  { key: "ops-summary", to: "/ops-summary", label: "Ops Summary" },
  { key: "skart-booking", to: "/skart-booking", label: "Skart Booking" },
  { key: "inxpress-bookings", to: "/inxpress-bookings", label: "Inxpress Bookings" },
  { key: "shipment-dashboard", to: "/dashboard", label: "Shipment Dashboard" },
  { key: "shipment-360", to: "/shipment-360", label: "Shipment 360" },
  { key: "cost-prices", to: "/cost-prices", label: "Cost Price Entry" },
  { key: "selling-prices", to: "/selling-prices", label: "Selling Price Entry" },
  { key: "vendor-rates", to: "/rates", label: "Vendor Rates" },
  { key: "pricing-console", to: "/pricing-console", label: "Pricing Console" },
  { key: "pricing-operations", to: "/pricing-operations", label: "Recalculate & AWB" },
  { key: "sales-tracker", to: "/sales-tracker", label: "Sales Tracker" },
  { key: "vendor-invoice-console", to: "/vendor-invoice-console", label: "Vendor Invoice Console" },
  { key: "accounts-receivable", to: "/accounts-receivable", label: "Accounts Receivable" },
  { key: "customer-invoicing", to: "/customer-invoicing", label: "Invoicing" },
  { key: "shipment-tracking", to: "/shipment-tracking", label: "Shipment Tracking" },
  { key: "master-data", to: "/master-data/customers", label: "Master Data" },
  { key: "user-management", to: "/user-management", label: "User Management" },
  { key: "tasks", to: "/tasks", label: "Tasks" },
  { key: "tickets", to: "/tickets", label: "Tickets" },
  { key: "attendance", to: "/attendance", label: "Attendance" },
  { key: "rate-card-generator", to: "/rate-card-generator", label: "Rate Cards" },
];

const ROLE_ACCESS = {
  admin: [
    "shipment-entry",
    "booking-queue",
    "pending-pickup",
    "checklists",
    "ops-summary",
    "skart-booking",
    "inxpress-bookings",
    "skart-activity",
    "skart-payload",
    "shipment-dashboard",
    "shipment-360",
    "cost-prices",
    "selling-prices",
    "vendor-rates",
    "pricing-console",
    "pricing-operations",
    "express-rate-calculator",
    "sales-tracker",
    "vendor-invoice-console",
    "accounts-receivable",
    "customer-invoicing",
    "shipment-tracking",
    "master-data",
    "customer-bookings",
    "user-management",
    "margin-dashboard",
    "tasks",
    "tickets",
    "attendance",
    "rate-card-generator",
  ],
  operations: [
    "shipment-entry",
    "booking-queue",
    "pending-pickup",
    "checklists",
    "ops-summary",
    "skart-booking",
    "inxpress-bookings",
    "skart-activity",
    "skart-payload",
    "shipment-dashboard",
    "express-rate-calculator",
    "sales-tracker",
    "shipment-tracking",
    "master-data",
    "customer-bookings",
    "tasks",
    "tickets",
    "attendance",
  ],
  finance: [
    "shipment-entry",
    "booking-queue",
    "pending-pickup",
    "checklists",
    "ops-summary",
    "skart-booking",
    "inxpress-bookings",
    "skart-activity",
    "skart-payload",
    "shipment-dashboard",
    "cost-prices",
    "selling-prices",
    "vendor-rates",
    "pricing-console",
    "pricing-operations",
    "express-rate-calculator",
    "vendor-invoice-console",
    "accounts-receivable",
    "customer-invoicing",
    "shipment-tracking",
    "master-data",
    "tasks",
    "tickets",
    "attendance",
    "rate-card-generator",
  ],
  sales: [
    "express-rate-calculator",
    "sales-tracker",
    "tasks",
    "tickets",
    "attendance",
  ]
};

const hasAccess = (user, screenKey) =>
  Boolean(user && ROLE_ACCESS[user.role]?.includes(screenKey));

function ProtectedRoute({ user, allowedRoles, element }) {
  if (user === undefined) {
    return <div className="container mt-5">Loading portal...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return (
      <div className="alert alert-danger mt-4">
        You do not have permission to access this page.
      </div>
    );
  }

  return element;
}

function HomeRoute({ user }) {
  if (user === undefined) {
    return <div className="container mt-5">Loading portal...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role === "sales") {
    return <Navigate to="/sales-tracker" replace />;
  }

  return <ShipmentEntry />;
}

function App() {
  // The TV board is a full screen of its own: no menu, no banners
  const isTvBoard = window.location.pathname === "/ops-tv";
  const isStandalone = isTvBoard || new URLSearchParams(window.location.search).has("standalone");

  const [auth, setAuth] = useState({
    loading: true,
    user: undefined,
    requestIp: ""
  });

  useEffect(() => {
    const bootstrap = async () => {
      try {
        const response = await API.get("/auth/me");
        setAuth({
          loading: false,
          user: response.data,
          requestIp: response.data.request_ip || ""
        });
      } catch (error) {
        setAuth({
          loading: false,
          user: null,
          requestIp: ""
        });
      }
    };

    bootstrap();
  }, []);

  const handleLogin = ({ token, user, request_ip }) => {
    setAuthToken(token);
    setAuth({
      loading: false,
      user,
      requestIp: request_ip || ""
    });
  };

  const handleLogout = async () => {
    try {
      await API.post("/auth/logout");
    } catch (error) {
      // Local session state should still be cleared even if the cookie logout fails.
    }
    setAuthToken("");
    setAuth({
      loading: false,
      user: null,
      requestIp: ""
    });
  };

  useEffect(() => {
    if (!auth.user) return undefined;

    const IDLE_LOGOUT_MS = 30 * 60 * 1000;
    let idleTimer = setTimeout(handleLogout, IDLE_LOGOUT_MS);
    const resetIdleTimer = () => {
      clearTimeout(idleTimer);
      idleTimer = setTimeout(handleLogout, IDLE_LOGOUT_MS);
    };
    const activityEvents = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "wheel"];
    activityEvents.forEach((eventName) => window.addEventListener(eventName, resetIdleTimer));

    return () => {
      clearTimeout(idleTimer);
      activityEvents.forEach((eventName) => window.removeEventListener(eventName, resetIdleTimer));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.user]);

  const tabClassName = ({ isActive }) =>
    isActive ? "app-nav-link active" : "app-nav-link";

  return (
    <Router>
      <div className="app-shell">
        {auth.user && !isStandalone ? (
          <nav className="app-header">
            <div className="app-header__inner">
              <div>
                <p className="app-header__eyebrow">Operations Portal</p>
                <div className="app-header__brand-stack">
                  <span className="app-header__brand">Ease My Shipping Logistics Pvt. Ltd.</span>
                  <span className="app-header__subbrand">Shipment Management Console</span>
                </div>
              </div>

              <div className="app-header__actions">
                <div className="app-user-chip">
                  <strong>{auth.user.display_name}</strong>
                  <span>
                    {auth.user.role}
                    {auth.requestIp ? ` | IP ${auth.requestIp}` : ""}
                  </span>
                </div>
                <button className="btn btn-outline-light" onClick={handleLogout}>
                  Logout
                </button>
              </div>
            </div>

            <div className="app-nav">
              {NAV_ITEMS.map((item) =>
                hasAccess(auth.user, item.key) ? (
                  item.key === "tasks" ? (
                    <a
                      key={item.key}
                      className="app-nav-link"
                      href="/tasks?standalone=true"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {item.label}
                    </a>
                  ) : item.external ? (
                    <a
                      key={item.key}
                      className="app-nav-link"
                      href={buildBackendPageUrl(item.href)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {item.label}
                    </a>
                  ) : (
                    <NavLink key={item.key} className={tabClassName} to={item.to} end={item.end}>
                      {item.label}
                    </NavLink>
                  )
                ) : null
              )}

              {hasAccess(auth.user, "margin-dashboard") ? (
                <a
                  className="app-nav-link app-nav-link--accent"
                  href={buildBackendPageUrl("/margin-dashboard")}
                  target="_blank"
                  rel="noreferrer"
                >
                  Margin Dashboard
                </a>
              ) : null}

              {hasAccess(auth.user, "customer-bookings") ? (
                <a
                  className="app-nav-link app-nav-link--accent"
                  href={buildBackendPageUrl("/internal/customer-bookings")}
                  target="_blank"
                  rel="noreferrer"
                >
                  Customer Bookings
                </a>
              ) : null}

              {hasAccess(auth.user, "margin-dashboard") ? (
                <a
                  className="app-nav-link app-nav-link--accent"
                  href={buildBackendPageUrl("/quote-activity")}
                  target="_blank"
                  rel="noreferrer"
                >
                  Quote Activity
                </a>
              ) : null}
            </div>
          </nav>
        ) : null}

        {auth.user && !isTvBoard ? <CheckInReminderBanner /> : null}

        <main className={isStandalone ? "" : "app-content container"} style={isStandalone ? { margin: 0, padding: 0, maxWidth: "none" } : {}}>
          <Suspense fallback={<div className="container mt-5">Loading...</div>}>
          <Routes>
            <Route
              path="/login"
              element={
                auth.user
                  ? <Navigate to="/" replace />
                  : <LoginPage onLogin={handleLogin} />
              }
            />
            <Route path="/" element={<HomeRoute user={auth.user} />} />
            {/* Opened by the TV with ?key=..., or by anyone logged in - the page checks */}
            <Route path="/ops-tv" element={<OpsTvPage />} />
            <Route path="/booking-queue" element={<ProtectedRoute user={auth.user} allowedRoles={["admin", "operations", "finance"]} element={<BookingQueuePage />} />} />
            <Route path="/pending-pickup" element={<ProtectedRoute user={auth.user} allowedRoles={["admin", "operations", "finance"]} element={<PendingPickupPage />} />} />
            <Route path="/checklists" element={<ProtectedRoute user={auth.user} allowedRoles={["admin", "operations", "finance"]} element={<ChecklistsPage />} />} />
            <Route path="/ops-summary" element={<ProtectedRoute user={auth.user} allowedRoles={["admin", "operations", "finance"]} element={<OpsSummaryPage />} />} />
            <Route path="/skart-booking" element={<ProtectedRoute user={auth.user} allowedRoles={["admin", "operations", "finance"]} element={<SkartBookingPage />} />} />
            <Route path="/inxpress-bookings" element={<ProtectedRoute user={auth.user} allowedRoles={["admin", "operations", "finance"]} element={<InxpressBookingsPage />} />} />
            <Route path="/skart-booking/activity" element={<ProtectedRoute user={auth.user} allowedRoles={["admin", "operations", "finance"]} element={<SkartActivityPage />} />} />
            <Route path="/skart-booking/payloads" element={<ProtectedRoute user={auth.user} allowedRoles={["admin", "operations", "finance"]} element={<SkartPayloadPage />} />} />
            <Route path="/dashboard" element={<ProtectedRoute user={auth.user} allowedRoles={["admin", "operations", "finance"]} element={<ShipmentDashboard />} />} />
            <Route path="/shipment-360" element={<ProtectedRoute user={auth.user} allowedRoles={["admin"]} element={<Shipment360Page />} />} />
            <Route path="/cost-prices" element={<ProtectedRoute user={auth.user} allowedRoles={["admin", "finance"]} element={<CostPriceEntry />} />} />
            <Route path="/selling-prices" element={<ProtectedRoute user={auth.user} allowedRoles={["admin", "finance"]} element={<SellingPriceEntry />} />} />
            <Route path="/rates" element={<ProtectedRoute user={auth.user} allowedRoles={["admin", "finance"]} element={<VendorRateUpload />} />} />
            <Route path="/pricing-console" element={<ProtectedRoute user={auth.user} allowedRoles={["admin", "finance"]} element={<PricingConsolePage />} />} />
            <Route path="/pricing-operations" element={<ProtectedRoute user={auth.user} allowedRoles={["admin", "finance"]} element={<PricingOperationsPage />} />} />
            <Route path="/sales-tracker" element={<ProtectedRoute user={auth.user} allowedRoles={["admin", "operations", "sales"]} element={<SalesTrackerPage />} />} />
            <Route path="/vendor-invoice-console" element={<ProtectedRoute user={auth.user} allowedRoles={["admin", "finance"]} element={<VendorInvoiceConsolePage />} />} />
            <Route path="/accounts-receivable" element={<ProtectedRoute user={auth.user} allowedRoles={["admin", "finance"]} element={<AccountsReceivablePage />} />} />
            <Route path="/customer-invoicing" element={<ProtectedRoute user={auth.user} allowedRoles={["admin", "finance"]} element={<InvoicingPage />} />} />
            {/* Shipment Tracking opens on the Manual Status Board; the automated tracker is a click away */}
            <Route path="/shipment-tracking" element={<ProtectedRoute user={auth.user} allowedRoles={["admin", "operations", "finance"]} element={<ManualStatusBoardPage />} />} />
            <Route path="/shipment-tracking/automated" element={<ProtectedRoute user={auth.user} allowedRoles={["admin", "operations", "finance"]} element={<ShipmentTrackingPage />} />} />
            <Route path="/shipment-tracking/in-transit" element={<ProtectedRoute user={auth.user} allowedRoles={["admin", "operations", "finance"]} element={<ShipmentTrackingInTransitPage />} />} />
            <Route path="/shipment-tracking/fedex" element={<ProtectedRoute user={auth.user} allowedRoles={["admin", "operations", "finance"]} element={<FedExTrackerPage />} />} />
            <Route path="/shipment-tracking/manual-board" element={<ProtectedRoute user={auth.user} allowedRoles={["admin", "operations", "finance"]} element={<ManualStatusBoardPage />} />} />
            <Route path="/shipment-tracking/email-templates" element={<ProtectedRoute user={auth.user} allowedRoles={["admin", "operations", "finance"]} element={<StatusEmailTemplatesPage />} />} />
            <Route path="/shipment-tracking/whatsapp-templates" element={<ProtectedRoute user={auth.user} allowedRoles={["admin", "operations", "finance"]} element={<WhatsappTemplatesPage />} />} />
            <Route path="/masters" element={<ProtectedRoute user={auth.user} allowedRoles={["admin", "operations", "finance"]} element={<MasterData />} />} />
            <Route path="/master-data/customers" element={<ProtectedRoute user={auth.user} allowedRoles={["admin", "operations", "finance"]} element={<MasterData />} />} />
            <Route path="/master-data/customers/:customerId" element={<ProtectedRoute user={auth.user} allowedRoles={["admin", "operations", "finance"]} element={<MasterData />} />} />
            <Route path="/user-management" element={<ProtectedRoute user={auth.user} allowedRoles={["admin"]} element={<UserManagementPage />} />} />
            <Route path="/tasks" element={<ProtectedRoute user={auth.user} allowedRoles={["admin", "operations", "finance", "sales"]} element={<TaskBoardPage />} />} />
            <Route path="/tickets" element={<ProtectedRoute user={auth.user} allowedRoles={["admin", "operations", "finance", "sales"]} element={<TicketingToolPage />} />} />
            <Route path="/attendance" element={<ProtectedRoute user={auth.user} allowedRoles={["admin", "operations", "finance", "sales"]} element={<AttendancePage />} />} />
            <Route path="/rate-card-generator" element={<ProtectedRoute user={auth.user} allowedRoles={["admin", "finance"]} element={<RateCardGeneratorPage />} />} />
          </Routes>
          </Suspense>
        </main>
      </div>
    </Router>
  );
}

export default App;
